import { render, button } from './components';
import { GameManager, ROUND_CONFIG } from '../game';
import { PlayerState, CardTier } from '../types';
import { getCardById } from '../cards';

export interface RoundTransitionCallbacks {
  onContinue: () => void;
}

// ラウンド終了→次ラウンド予告画面
export function renderRoundTransition(
  game: GameManager,
  completedRound: number,
  callbacks: RoundTransitionCallbacks
): void {
  const nextRound = completedRound + 1;
  const gameState = game.getState();
  const totalRounds = gameState.config.qualifyingRounds;
  const isLastQualifying = nextRound > totalRounds;
  
  const ranking = game.getRanking();
  const activePlayers = ranking.filter((r) => !r.player.isEliminated);
  
  // 次ラウンドの情報(configのtierDrawsを優先)
  const tierInfo = getTierInfoForRound(nextRound, gameState.config.tierDraws);
  
  render(`
    <div class="round-transition" style="
      padding: 20px 16px;
      max-width: 600px;
      margin: 0 auto;
      min-height: 100vh;
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    ">
      <!-- ラウンド完了 -->
      <div style="
        animation: slideDown 0.5s ease-out;
        margin-bottom: 24px;
      ">
        <div style="
          color: #2ecc71;
          font-size: 0.9rem;
          margin-bottom: 8px;
        ">✓ ROUND ${completedRound} 完了</div>
        
        ${isLastQualifying ? `
          <h1 style="
            color: #ffd700;
            font-size: clamp(1.8rem, 6vw, 2.5rem);
            text-shadow: 0 0 30px rgba(255,215,0,0.5);
            margin: 0;
          ">🏆 予選終了!</h1>
          <p style="color: #aaa; margin-top: 8px;">決勝進出者が決定しました</p>
        ` : `
          <h1 style="
            color: #00fff5;
            font-size: clamp(1.8rem, 6vw, 2.5rem);
            text-shadow: 0 0 30px rgba(0,255,245,0.5);
            margin: 0;
          ">ROUND ${nextRound}</h1>
          <p style="color: #aaa; margin-top: 8px;">残り ${totalRounds - completedRound} ラウンド</p>
        `}
      </div>

      <!-- 現在のランキング (トップ4) -->
      <div style="
        background: rgba(0,0,0,0.3);
        border-radius: 12px;
        padding: 16px;
        width: 100%;
        max-width: 400px;
        margin-bottom: 20px;
        animation: fadeIn 0.5s ease-out 0.2s both;
      ">
        <h3 style="color: #ffd700; margin: 0 0 12px 0; font-size: 0.95rem;">
          📊 現在の順位
        </h3>
        
        ${ranking.slice(0, 4).map((r, i) => {
          const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '';
          const isFinalist = i < 2;
          const wins = r.player.matchHistory.filter(m => m.result === 'win').length;
          
          return `
            <div style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 8px 10px;
              margin: 4px 0;
              background: ${isFinalist ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.03)'};
              border-radius: 6px;
              ${isFinalist ? 'border: 1px solid rgba(255,215,0,0.3);' : ''}
            ">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="width: 24px; font-size: 0.9rem;">${medal || `#${r.rank}`}</span>
                <span>${r.player.info.type === 'bot' ? '🤖' : '👤'}</span>
                <span style="font-size: 0.85rem;">${r.player.info.name}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 10px;">
                <span style="color: #666; font-size: 0.7rem;">${wins}W</span>
                <span style="color: #ffd700; font-weight: bold;">★ ${r.player.fans}</span>
              </div>
            </div>
          `;
        }).join('')}
        
        ${ranking.length > 4 ? `
          <div style="color: #666; font-size: 0.75rem; margin-top: 8px; text-align: center;">
            他 ${ranking.length - 4} 名...
          </div>
        ` : ''}
      </div>

      ${!isLastQualifying ? `
        <!-- 次ラウンドの情報 -->
        <div style="
          background: linear-gradient(135deg, rgba(0,255,245,0.1) 0%, rgba(0,188,212,0.05) 100%);
          border: 1px solid rgba(0,255,245,0.3);
          border-radius: 12px;
          padding: 16px;
          width: 100%;
          max-width: 400px;
          margin-bottom: 24px;
          animation: fadeIn 0.5s ease-out 0.4s both;
        ">
          <h3 style="color: #00fff5; margin: 0 0 12px 0; font-size: 0.95rem;">
            📦 Round ${nextRound} の獲得枠
          </h3>
          
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;">
            ${renderTierPreview(tierInfo)}
          </div>
          
          <div style="
            color: #888;
            font-size: 0.75rem;
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px solid rgba(255,255,255,0.1);
          ">
            💡 ${getTierHint(nextRound)}
          </div>
        </div>
      ` : ''}

      <!-- 続けるボタン -->
      <button id="btnContinue" class="btn" style="
        background: linear-gradient(135deg, #00fff5 0%, #00bcd4 100%);
        border: none;
        color: #1a1a2e;
        padding: 14px 40px;
        border-radius: 8px;
        font-size: 1.1rem;
        font-weight: bold;
        cursor: pointer;
        min-height: 52px;
        animation: fadeIn 0.5s ease-out 0.6s both;
      ">${isLastQualifying ? '🏆 決勝戦へ' : '➡️ デッキ構築へ'}</button>
    </div>
  `);

  // アニメーションCSS追加
  injectTransitionStyles();

  document.getElementById('btnContinue')?.addEventListener('click', callbacks.onContinue);
}

// Tier情報取得
interface TierDrawInfo {
  tier: 'A' | 'B' | 'C';
  count: number;
  color: string;
}

function getTierInfoForRound(round: number, customTierDraws?: Record<CardTier, number>[]): TierDrawInfo[] {
  const colors: Record<'A' | 'B' | 'C', string> = { A: '#2ecc71', B: '#3498db', C: '#9b59b6' };

  // カスタム設定があればそれを使用
  if (customTierDraws && customTierDraws[round - 1]) {
    const draws = customTierDraws[round - 1];
    const result: TierDrawInfo[] = [];
    const tiers: ('A' | 'B' | 'C')[] = ['A', 'B', 'C'];
    for (const tier of tiers) {
      if (draws[tier] > 0) {
        result.push({ tier, count: draws[tier], color: colors[tier] });
      }
    }
    return result;
  }

  // ROUND_CONFIGから取得(デフォルト)
  const config = ROUND_CONFIG[round] ?? ROUND_CONFIG[7];
  return config.availableTiers.map(opt => ({
    tier: opt.tier as 'A' | 'B' | 'C',
    count: opt.selectCount,
    color: colors[opt.tier as 'A' | 'B' | 'C']
  }));
}

function renderTierPreview(tierInfo: TierDrawInfo[]): string {
  return tierInfo.map((t) => `
    <div style="
      background: ${t.color}20;
      border: 1px solid ${t.color};
      border-radius: 8px;
      padding: 10px;
      text-align: center;
    ">
      <div style="
        color: ${t.color};
        font-size: 1.2rem;
        font-weight: bold;
      ">Tier ${t.tier}</div>
      <div style="
        color: #fff;
        font-size: 1.5rem;
        font-weight: bold;
        margin-top: 4px;
      ">×${t.count}</div>
    </div>
  `).join('');
}

function getTierHint(round: number): string {
  if (round <= 2) return 'Tier A: 基本カードを集めてデッキを安定させよう';
  if (round <= 4) return 'Tier B: 能力付きカードでシナジーを狙おう';
  if (round <= 6) return 'Tier C: 強力なカードで勝負を決めよう';
  return 'Tier C: 最強カードを手に入れるラストチャンス!';
}

// マッチプレビュー画面 (次の対戦相手表示)
export function renderMatchPreview(
  myPlayer: PlayerState,
  opponent: PlayerState,
  round: number,
  callbacks: { onStart: () => void }
): void {
  const myWins = myPlayer.matchHistory.filter((m) => m.result === 'win').length;
  const oppWins = opponent.matchHistory.filter((m) => m.result === 'win').length;
  
  // 過去の直接対決
  const directMatch = myPlayer.matchHistory.find((m) => m.opponentId === opponent.info.id);
  
  render(`
    <div class="match-preview" style="
      padding: 20px 16px;
      max-width: 600px;
      margin: 0 auto;
      min-height: 100vh;
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        color: #666;
        font-size: 0.9rem;
        margin-bottom: 8px;
      ">Round ${round}</div>
      
      <h1 style="
        color: #ff6b6b;
        font-size: clamp(1.5rem, 5vw, 2rem);
        text-shadow: 0 0 20px rgba(255,107,107,0.5);
        margin: 0 0 30px 0;
        letter-spacing: 4px;
      ">NEXT MATCH</h1>

      <!-- VS表示 -->
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        margin-bottom: 30px;
        width: 100%;
      ">
        <!-- 自分 -->
        <div style="
          flex: 1;
          max-width: 180px;
          text-align: center;
          padding: 20px;
          background: rgba(0,255,245,0.1);
          border: 2px solid #00fff5;
          border-radius: 12px;
        ">
          <div style="font-size: 2.5rem; margin-bottom: 8px;">
            ${myPlayer.info.type === 'bot' ? '🤖' : '👤'}
          </div>
          <div style="
            font-size: 1rem;
            font-weight: bold;
            color: #00fff5;
            margin-bottom: 4px;
          ">${myPlayer.info.name}</div>
          <div style="color: #ffd700; font-size: 0.9rem;">★ ${myPlayer.fans}</div>
          <div style="color: #666; font-size: 0.75rem; margin-top: 4px;">${myWins}勝</div>
        </div>

        <!-- VS -->
        <div style="
          font-size: 2rem;
          color: #ff6b6b;
          text-shadow: 0 0 20px #ff6b6b;
          animation: pulse 1.5s ease-in-out infinite;
        ">VS</div>

        <!-- 対戦相手 -->
        <div style="
          flex: 1;
          max-width: 180px;
          text-align: center;
          padding: 20px;
          background: rgba(255,107,107,0.1);
          border: 2px solid #ff6b6b;
          border-radius: 12px;
        ">
          <div style="font-size: 2.5rem; margin-bottom: 8px;">
            ${opponent.info.type === 'bot' ? '🤖' : '👤'}
          </div>
          <div style="
            font-size: 1rem;
            font-weight: bold;
            color: #ff6b6b;
            margin-bottom: 4px;
          ">${opponent.info.name}</div>
          <div style="color: #ffd700; font-size: 0.9rem;">★ ${opponent.fans}</div>
          <div style="color: #666; font-size: 0.75rem; margin-top: 4px;">${oppWins}勝</div>
        </div>
      </div>

      ${directMatch ? `
        <div style="
          background: rgba(0,0,0,0.3);
          border-radius: 8px;
          padding: 10px 16px;
          margin-bottom: 24px;
          font-size: 0.85rem;
        ">
          <span style="color: #888;">過去の対戦:</span>
          <span style="
            color: ${directMatch.result === 'win' ? '#2ecc71' : '#e74c3c'};
            margin-left: 8px;
          ">
            ${directMatch.result === 'win' ? '勝利 ✓' : '敗北 ✗'}
          </span>
        </div>
      ` : ''}

      <button id="btnStartMatch" class="btn" style="
        background: linear-gradient(135deg, #ff6b6b 0%, #e74c3c 100%);
        border: none;
        color: #fff;
        padding: 14px 48px;
        border-radius: 8px;
        font-size: 1.1rem;
        font-weight: bold;
        cursor: pointer;
        min-height: 52px;
      ">⚔️ FIGHT!</button>
    </div>
  `);

  // アニメーションCSS追加
  injectTransitionStyles();

  document.getElementById('btnStartMatch')?.addEventListener('click', callbacks.onStart);
}

// 決勝進出者発表画面
export function renderFinalistsAnnouncement(
  finalists: PlayerState[],
  allPlayers: PlayerState[],
  callbacks: { onContinue: () => void }
): void {
  render(`
    <div class="finalists-announcement" style="
      padding: 20px 16px;
      max-width: 600px;
      margin: 0 auto;
      min-height: 100vh;
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    ">
      <div style="animation: slideDown 0.5s ease-out;">
        <h1 style="
          color: #ffd700;
          font-size: clamp(2rem, 8vw, 3rem);
          text-shadow: 0 0 40px rgba(255,215,0,0.5);
          margin: 0 0 8px 0;
        ">🏆 FINALS 🏆</h1>
        <p style="color: #aaa; margin-bottom: 30px;">決勝進出者</p>
      </div>

      <!-- 決勝進出者 -->
      <div style="
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 30px;
        margin-bottom: 30px;
        flex-wrap: wrap;
      ">
        ${finalists.map((p, i) => `
          <div style="
            animation: fadeIn 0.5s ease-out ${0.3 + i * 0.2}s both;
            text-align: center;
          ">
            <div style="
              width: 100px;
              height: 100px;
              background: linear-gradient(135deg, #ffd700 0%, #f39c12 100%);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 3rem;
              margin: 0 auto 12px auto;
              box-shadow: 0 0 30px rgba(255,215,0,0.5);
            ">
              ${p.info.type === 'bot' ? '🤖' : '👤'}
            </div>
            <div style="
              font-size: 1.2rem;
              font-weight: bold;
              color: #fff;
            ">${p.info.name}</div>
            <div style="color: #ffd700; font-size: 1rem;">★ ${p.fans}</div>
            <div style="color: #888; font-size: 0.8rem;">
              ${p.matchHistory.filter(m => m.result === 'win').length}勝
            </div>
          </div>
        `).join('')}
      </div>

      <!-- 惜しくも敗退 -->
      <div style="
        background: rgba(0,0,0,0.3);
        border-radius: 12px;
        padding: 16px;
        width: 100%;
        max-width: 400px;
        margin-bottom: 24px;
        animation: fadeIn 0.5s ease-out 0.7s both;
      ">
        <div style="color: #666; font-size: 0.8rem; margin-bottom: 10px;">
          惜しくも決勝進出ならず
        </div>
        <div style="display: flex; flex-wrap: wrap; gap: 8px; justify-content: center;">
          ${allPlayers
            .filter((p) => !finalists.some((f) => f.info.id === p.info.id))
            .slice(0, 6)
            .map((p) => `
              <span style="
                background: rgba(255,255,255,0.05);
                padding: 4px 10px;
                border-radius: 12px;
                font-size: 0.75rem;
                color: #888;
              ">
                ${p.info.type === 'bot' ? '🤖' : '👤'} ${p.info.name} (★${p.fans})
              </span>
            `).join('')}
        </div>
      </div>

      <button id="btnToFinals" class="btn" style="
        background: linear-gradient(135deg, #ffd700 0%, #f39c12 100%);
        border: none;
        color: #1a1a2e;
        padding: 14px 48px;
        border-radius: 8px;
        font-size: 1.1rem;
        font-weight: bold;
        cursor: pointer;
        min-height: 52px;
        animation: fadeIn 0.5s ease-out 0.9s both;
      ">⚔️ 決勝戦開始!</button>
    </div>
  `);

  injectTransitionStyles();

  document.getElementById('btnToFinals')?.addEventListener('click', callbacks.onContinue);
}

// アニメーションCSS
function injectTransitionStyles(): void {
  if (document.getElementById('transition-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'transition-styles';
  style.textContent = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.8; transform: scale(1.05); }
    }
  `;
  document.head.appendChild(style);
}
